import {
  AppBar, Toolbar, Typography, Box, Container, Drawer, List,
  ListItemButton, ListItemText, ListItemIcon, Chip, Badge, IconButton,
  Menu, MenuItem, Divider,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const DRAWER_WIDTH = 250;
const HEADER_HEIGHT = 72;

export default function DashboardLayout({ title, nav = [], welcomeName, children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchor, setAnchor] = useState(null);

  // Notifications (bell).
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [unread, setUnread] = useState(0);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifs(data.data.items || []);
      setUnread(data.data.unread || 0);
    } catch {
      // silent — the bell is non-critical
    }
  };
  useEffect(() => { loadNotifs(); }, []);

  const openNotifs = async (e) => {
    setNotifAnchor(e.currentTarget);
    if (unread > 0) {
      setUnread(0);
      try { await api.post('/notifications/read'); } catch { /* ignore */ }
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/portal');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: '#FFFFFF',
          borderBottom: '1px solid #E4E8E4',
        }}
      >
        <Toolbar sx={{ minHeight: `${HEADER_HEIGHT}px !important` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Box component="img" src="/logo.png" alt="Taraba State" sx={{ height: 44, width: 44 }} />
            <Typography variant="h6" sx={{ color: 'text.primary' }}>
              {welcomeName ? `Welcome, ${welcomeName}` : 'Taraba State E-Procurement Portal'}
            </Typography>
          </Box>

          <IconButton color="primary" size="large" aria-label="notifications" onClick={openNotifs}>
            <Badge badgeContent={unread} color="secondary"><NotificationsNoneIcon /></Badge>
          </IconButton>
          <Menu
            anchorEl={notifAnchor}
            open={!!notifAnchor}
            onClose={() => setNotifAnchor(null)}
            slotProps={{ paper: { sx: { width: 360, maxHeight: 420 } } }}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <Typography variant="subtitle2">Notifications</Typography>
            </MenuItem>
            <Divider />
            {notifs.length === 0 && (
              <MenuItem disabled sx={{ opacity: '1 !important' }}>
                <Typography variant="body2" color="text.secondary">No notifications yet.</Typography>
              </MenuItem>
            )}
            {notifs.map((n) => (
              <MenuItem
                key={n.id}
                onClick={() => { setNotifAnchor(null); if (n.link) navigate(n.link); }}
                sx={{ display: 'block', whiteSpace: 'normal', py: 1.2, bgcolor: n.readAt ? 'transparent' : 'action.hover' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{n.title}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{n.body}</Typography>
                <Typography variant="caption" color="text.disabled">{new Date(n.createdAt).toLocaleString('en-GB')}</Typography>
              </MenuItem>
            ))}
          </Menu>

          <IconButton color="primary" size="large" onClick={(e) => setAnchor(e.currentTarget)} aria-label="profile">
            <AccountCircleIcon />
          </IconButton>
          <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            <MenuItem disabled>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{user?.fullName}</Typography>
                <Typography variant="caption" color="text.secondary">{user?.role?.replace('_', ' ')}</Typography>
              </Box>
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {nav.length > 0 && (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH, flexShrink: 0,
            ['& .MuiDrawer-paper']: { width: DRAWER_WIDTH, boxSizing: 'border-box' },
          }}
        >
          <Box sx={{ height: `${HEADER_HEIGHT}px` }} />
          <List sx={{ pt: 0 }}>
            {nav.map((item) => (
              <ListItemButton
                key={item.label}
                selected={location.pathname === item.path}
                disabled={item.disabled}
                onClick={() => item.path && navigate(item.path)}
              >
                {item.icon && <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>}
                <ListItemText primary={item.label} />
                {item.disabled && <Chip label="Soon" size="small" variant="outlined" sx={{ height: 20, fontSize: 11 }} />}
              </ListItemButton>
            ))}
          </List>
        </Drawer>
      )}

      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Box sx={{ height: `${HEADER_HEIGHT}px` }} />
        <Container maxWidth="lg" disableGutters>
          {title && <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, fontSize: '1.35rem' }}>{title}</Typography>}
          {children}
        </Container>
      </Box>
    </Box>
  );
}
